import React, { useState, useRef, useEffect, useCallback } from 'react';
import { Typography, makeStyles, Button, Link, ButtonGroup } from '@material-ui/core';
import { maskPhoneNumber, getAUPhoneType } from '../../../utils/formatPhoneE164';
import formatPhoneE164 from '../../../utils/formatPhoneE164';
import { OTP_API_BASE_URL, OTP_API_SUBSCRIPTION_KEY } from '../../../constants';

// ─── OTP API functions ───────────────────────────────────────────────────────
async function sendOtp(
  phone: string,
  channel: 'sms' | 'call',
  firstName: string,
  lastName: string,
  postcode: string
): Promise<{ success: boolean; blocked?: boolean; status?: string; error?: string }> {
  const res = await fetch(`${OTP_API_BASE_URL}/generateOtp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Ocp-Apim-Subscription-Key': OTP_API_SUBSCRIPTION_KEY },
    body: JSON.stringify({ to: formatPhoneE164(phone), channel, firstName, lastName, postcode }),
  });
  return res.json();
}

async function checkOtp(phone: string, code: string): Promise<{ success: boolean; status?: string }> {
  const res = await fetch(`${OTP_API_BASE_URL}/verifyOtp`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json', 'Ocp-Apim-Subscription-Key': OTP_API_SUBSCRIPTION_KEY },
    body: JSON.stringify({ to: formatPhoneE164(phone), code }),
  });
  return res.json();
}
// ───────────────────────────────────────────────────────────────────────────────

const RESEND_COOLDOWN = 30; // seconds
const MAX_RESEND_ATTEMPTS = 5;

const useStyles = makeStyles(theme => ({
  container: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  title: {
    marginBottom: '0.5em',
    fontWeight: 600,
  },
  subtitle: {
    color: 'rgb(100, 100, 100)',
    marginBottom: '1.5em',
    textAlign: 'center',
  },
  channelGroup: {
    marginBottom: '2.5em',
    [theme.breakpoints.down('sm')]: {
      marginBottom: '3em',
    },
  },
  channelButton: {
    textTransform: 'none' as const,
    padding: '6px 18px',
    fontSize: '0.85rem',
  },
  channelButtonSelected: {
    backgroundColor: '#027AC5',
    color: '#fff',
    '&:hover': {
      backgroundColor: '#0267a8',
    },
  },
  otpInputContainer: {
    display: 'flex',
    gap: '12px',
    marginBottom: '1.5em',
    justifyContent: 'center',
  },
  otpBox: {
    width: '56px',
    height: '64px',
    border: '2px solid rgb(136, 140, 142)',
    borderRadius: '8px',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontSize: '1.5rem',
    fontWeight: 600,
    color: 'rgb(40, 42, 43)',
    transition: 'border-color 0.2s',
  },
  otpBoxActive: {
    borderColor: '#027AC5',
    boxShadow: '0 0 0 1px #027AC5',
  },
  otpBoxFilled: {
    borderColor: '#027AC5',
  },
  hiddenInput: {
    position: 'absolute' as const,
    opacity: 0,
    width: '1px',
    height: '1px',
    overflow: 'hidden',
  },
  errorText: {
    color: '#E22525',
    marginBottom: '1em',
    textAlign: 'center' as const,
  },
  successText: {
    color: '#4caf50',
    marginBottom: '1em',
    textAlign: 'center' as const,
  },
  resendSection: {
    textAlign: 'center' as const,
    marginBottom: '0.5em',
  },
  linkButton: {
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  goBack: {
    marginTop: '1em',
    textAlign: 'center' as const,
  },
  sendButton: {
    marginBottom: '0.75em',
    minWidth: '180px',
    padding: '10px 24px',
    fontSize: '1rem',
  },
  loadingText: {
    color: 'rgb(100, 100, 100)',
    marginBottom: '1em',
  },
}));

type OtpChannel = 'sms' | 'call';

interface OTPVerificationScreenProps {
  phonenumber: string;
  firstName: string;
  lastName: string;
  postcode: string;
  onVerified: () => void;
  onBack: () => void;
}

export default function OTPVerificationScreen({
  phonenumber,
  firstName,
  lastName,
  postcode,
  onVerified,
  onBack,
}: OTPVerificationScreenProps) {
  const classes = useStyles();
  const phoneType = getAUPhoneType(phonenumber);
  const isLandline = phoneType === 'landline';
  const [stage, setStage] = useState<'presend' | 'postsend' | 'blocked'>('presend');
  const [channel, setChannel] = useState<OtpChannel>(isLandline ? 'call' : 'sms');
  const [otpValue, setOtpValue] = useState('');
  const [error, setError] = useState('');
  const [sending, setSending] = useState(false);
  const [checking, setChecking] = useState(false);
  const [resendCount, setResendCount] = useState(0);
  const [cooldownEnd, setCooldownEnd] = useState<number>(0);
  const [cooldownRemaining, setCooldownRemaining] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const checkTimerRef = useRef<ReturnType<typeof setTimeout>>();
  const maskedPhone = maskPhoneNumber(phonenumber);

  // Cooldown timer
  useEffect(() => {
    if (cooldownEnd <= 0) return;
    const tick = () => {
      const remaining = Math.max(0, Math.ceil((cooldownEnd - Date.now()) / 1000));
      setCooldownRemaining(remaining);
    };
    tick();
    const interval = setInterval(tick, 500);
    return () => clearInterval(interval);
  }, [cooldownEnd]);

  const startCooldown = useCallback(() => {
    setCooldownEnd(Date.now() + RESEND_COOLDOWN * 1000);
  }, []);

  const handleSendCode = useCallback(async () => {
    setError('');
    setSending(true);
    try {
      const result = await sendOtp(phonenumber, channel, firstName, lastName, postcode);
      if (result.blocked) {
        setStage('blocked');
      } else if (result.success) {
        setStage('postsend');
        setOtpValue('');
        startCooldown();
        setTimeout(() => inputRef.current?.focus(), 100);
      } else {
        setError('Failed to send code. Please check your number and try again.');
      }
    } catch {
      setError('Failed to send code. Please check your number and try again.');
    } finally {
      setSending(false);
    }
  }, [phonenumber, channel, firstName, lastName, postcode, startCooldown]);

  const handleCheckOtp = useCallback(
    async (code: string) => {
      setError('');
      setChecking(true);
      try {
        const result = await checkOtp(phonenumber, code);
        if (result.success) {
          onVerified();
        } else if (result.status === 'max_attempts_reached') {
          setError('Too many incorrect attempts. Please go back and try again.');
        } else if (result.status === 'expired') {
          setError('Code has expired. Please request a new one.');
          setOtpValue('');
          setTimeout(() => inputRef.current?.focus(), 100);
        } else {
          setError('Incorrect code. Please try again.');
          setOtpValue('');
          setTimeout(() => inputRef.current?.focus(), 100);
        }
      } catch {
        setError('Verification failed. Please try again.');
        setOtpValue('');
        setTimeout(() => inputRef.current?.focus(), 100);
      } finally {
        setChecking(false);
      }
    },
    [phonenumber, onVerified]
  );

  const handleResend = useCallback(async () => {
    if (resendCount >= MAX_RESEND_ATTEMPTS) {
      setError('Too many attempts. Please go back and try again.');
      return;
    }
    setResendCount(prev => prev + 1);
    setError('');
    setSending(true);
    try {
      const result = await sendOtp(phonenumber, channel, firstName, lastName, postcode);
      if (result.success) {
        setOtpValue('');
        startCooldown();
        setTimeout(() => inputRef.current?.focus(), 100);
      } else {
        setError('Failed to resend code. Please try again.');
      }
    } catch {
      setError('Failed to resend code. Please try again.');
    } finally {
      setSending(false);
    }
  }, [phonenumber, channel, firstName, lastName, postcode, resendCount, startCooldown]);

  const handleChannelFallback = useCallback(async () => {
    const newChannel: OtpChannel = channel === 'sms' ? 'call' : 'sms';
    setChannel(newChannel);
    setResendCount(prev => prev + 1);
    setError('');
    setSending(true);
    try {
      const result = await sendOtp(phonenumber, newChannel, firstName, lastName, postcode);
      if (result.success) {
        setOtpValue('');
        startCooldown();
        setTimeout(() => inputRef.current?.focus(), 100);
      } else {
        setError('Failed to send code. Please try again.');
      }
    } catch {
      setError('Failed to send code. Please try again.');
    } finally {
      setSending(false);
    }
  }, [phonenumber, channel, firstName, lastName, postcode, startCooldown]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const val = e.target.value.replace(/\D/g, '').slice(0, 4);
    setOtpValue(val);
    setError('');
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    if (val.length === 4) {
      checkTimerRef.current = setTimeout(() => handleCheckOtp(val), 300);
    }
  };

  const handleInputKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && otpValue.length === 0) {
      e.preventDefault();
    }
  };

  const handleGoBack = () => {
    setStage('presend');
    setOtpValue('');
    setError('');
    setResendCount(0);
    setCooldownEnd(0);
    if (checkTimerRef.current) clearTimeout(checkTimerRef.current);
    onBack();
  };

  // ─── Blocked State ──────────────────────────────────────────────────
  if (stage === 'blocked') {
    return (
      <div className={classes.container}>
        <Typography variant="h6" className={classes.title}>
          Unable to verify
        </Typography>
        <Typography variant="body1" className={classes.subtitle}>
          This phone number has been blocked.
          <br />
          If you believe this is an error, please contact us.
        </Typography>
        <Button variant="contained" color="primary" onClick={handleGoBack}>
          Go Back
        </Button>
      </div>
    );
  }

  // ─── Stage 1: Pre-Send ───────────────────────────────────────────────
  if (stage === 'presend') {
    return (
      <div className={classes.container}>
        <Typography variant="h6" className={classes.title}>
          Verify your phone number
        </Typography>
        <Typography variant="body1" className={classes.subtitle}>
          We'll send a verification code to
          <br />
          <strong>{maskedPhone}</strong>
        </Typography>

        {isLandline ? (
          <Typography variant="body2" style={{ marginBottom: '1.5em', color: 'rgb(100, 100, 100)' }}>
            You will receive a phone call with your verification code.
          </Typography>
        ) : (
          <>
            <Typography variant="body2" style={{ marginBottom: '0.5em', color: 'rgb(100, 100, 100)' }}>
              How would you like to receive your code?
            </Typography>

            <ButtonGroup variant="outlined" color="primary" className={classes.channelGroup}>
              <Button
                className={`${classes.channelButton} ${channel === 'sms' ? classes.channelButtonSelected : ''}`}
                onClick={() => setChannel('sms')}
              >
                Text message (SMS)
              </Button>
              <Button
                className={`${classes.channelButton} ${channel === 'call' ? classes.channelButtonSelected : ''}`}
                onClick={() => setChannel('call')}
              >
                Phone call
              </Button>
            </ButtonGroup>
          </>
        )}

        {error && (
          <Typography variant="body2" className={classes.errorText}>
            {error}
          </Typography>
        )}

        <Button
          variant="contained"
          color="primary"
          className={classes.sendButton}
          onClick={handleSendCode}
          disabled={sending}
        >
          {sending ? (channel === 'call' ? 'Calling...' : 'Sending...') : channel === 'call' ? 'Call Me' : 'Send Code'}
        </Button>

        <div className={classes.goBack}>
          <Typography variant="body2">
            Wrong number?{' '}
            <Link component="button" className={classes.linkButton} onClick={handleGoBack}>
              Go back
            </Link>
          </Typography>
        </div>
      </div>
    );
  }

  // ─── Stage 2: Post-Send ──────────────────────────────────────────────
  const channelLabel = channel === 'sms' ? 'SMS' : 'phone call';
  const fallbackLabel = channel === 'sms' ? 'Call me instead' : 'Try SMS instead';

  return (
    <div className={classes.container}>
      <Typography variant="h6" className={classes.title}>
        Enter verification code
      </Typography>
      <Typography variant="body1" className={classes.subtitle}>
        A 4-digit code was sent to
        <br />
        <strong>{maskedPhone}</strong> via {channelLabel}
      </Typography>

      {/* Hidden real input */}
      <input
        ref={inputRef}
        type="text"
        inputMode="numeric"
        autoComplete="one-time-code"
        value={otpValue}
        onChange={handleInputChange}
        onKeyDown={handleInputKeyDown}
        className={classes.hiddenInput}
        maxLength={4}
        disabled={checking}
        autoFocus
      />

      {/* Visual OTP boxes */}
      <div className={classes.otpInputContainer} onClick={() => inputRef.current?.focus()} style={{ cursor: 'text' }}>
        {[0, 1, 2, 3].map(i => {
          const isCurrent = i === otpValue.length;
          const isFilled = i < otpValue.length;
          return (
            <div
              key={i}
              className={`${classes.otpBox} ${isCurrent ? classes.otpBoxActive : ''} ${
                isFilled ? classes.otpBoxFilled : ''
              }`}
            >
              {otpValue[i] || ''}
            </div>
          );
        })}
      </div>

      {checking && (
        <Typography variant="body2" className={classes.loadingText}>
          Verifying...
        </Typography>
      )}

      {error && (
        <Typography variant="body2" className={classes.errorText}>
          {error}
        </Typography>
      )}

      {/* Resend section */}
      <div className={classes.resendSection}>
        {resendCount >= MAX_RESEND_ATTEMPTS ? (
          <Typography variant="body2" className={classes.errorText}>
            Too many attempts. Please go back and try again.
          </Typography>
        ) : cooldownRemaining > 0 ? (
          <Typography variant="body2" style={{ color: 'rgb(100, 100, 100)', fontSize: '0.85rem' }}>
            Resend code in 0:{cooldownRemaining.toString().padStart(2, '0')}
          </Typography>
        ) : (
          <Link component="button" className={classes.linkButton} onClick={handleResend} disabled={sending}>
            {sending ? 'Sending...' : 'Resend code'}
          </Link>
        )}
      </div>

      {/* Channel fallback — not shown for landlines (voice only) */}
      {!isLandline && resendCount < MAX_RESEND_ATTEMPTS && (
        <div style={{ marginTop: '0.25em', textAlign: 'center' }}>
          <Typography variant="body2" style={{ fontSize: '0.85rem' }}>
            Didn't get it?{' '}
            <Link
              component="button"
              className={classes.linkButton}
              onClick={handleChannelFallback}
              disabled={sending || cooldownRemaining > 0}
            >
              {fallbackLabel}
            </Link>
          </Typography>
        </div>
      )}

      <div className={classes.goBack}>
        <Typography variant="body2">
          Wrong number?{' '}
          <Link component="button" className={classes.linkButton} onClick={handleGoBack}>
            Go back
          </Link>
        </Typography>
      </div>
    </div>
  );
}
